import {
  IField_Role_Scope,
} from '../types/schema/role'

import {
  ITree_Role,
  INode_Role,
} from '../types/schema/tree'

import {
  IData_UserSummary,
  IData_UserSummary_Role,
  IData_UserSummary_FlatRoleObject,
  IData_RoleSummary_TreeNode,
} from '../types/schema/user_summary'

import {
  ROLE_SCOPE_CHILDREN,
  ROLE_SCOPE_DESCENDENTS,
  ROLE_SCOPE_SELF,
  ROLE_SCOPE_NONE,
  ROLE_SCOPE_ALL,
  ROLE_SCOPE_LEVELS,
  ROLE_ORIGIN_TYPE_USER,
  ROLE_ORIGIN_TYPE_USER_PERSONA,
} from '../constants'

import {
  roleHooks,
} from './hooks'

export const hasRole = (
  summary: IData_UserSummary | null,
  role: string,
): boolean => {
  if(!summary) return false
  const defaultValue = summary.roles[role] ? true : false
  const hook = roleHooks[summary.project_name]
  if(!hook) return defaultValue
  const [ handled, value ] = hook(summary, role)
  if(!handled) return defaultValue
  return value
}

export const hasRoleForOrg = (
  summary: IData_UserSummary | null,
  role: string,
  orgid: string,
): boolean => {
  if(!summary) return false
  const roleData = summary.roles[role]
  return roleData.orgs.indexOf(orgid) > -1
}

// adjust the scope for looking into
// org_path based resources
// e.g. if the user org is set to 'CHILDREN'
// and we are asking for descendents below
// a child - we adjust accordingly
export const adjustScope = (
  userOrgPath: string,
  targetOrgPath: string,
  roleScope: IField_Role_Scope,
  requestedScope: IField_Role_Scope,
): IField_Role_Scope => {
  // they have 'ALL' scope so can continue with what they requested
  if(roleScope == ROLE_SCOPE_ALL) return requestedScope
  
  // how many levels down from the user org are we looking?
  const sourceLevel = userOrgPath.split('.').length
  const targetLevel = targetOrgPath.split('.').length
  
  // prevent looking upwards
  if(targetLevel < sourceLevel) return ROLE_SCOPE_NONE

  const levelGap = targetLevel - sourceLevel

  // we limit what they ask for to what they can have
  const minimizedScope = ROLE_SCOPE_LEVELS[roleScope] < ROLE_SCOPE_LEVELS[requestedScope] ?
    roleScope :
    requestedScope

  // if we are asking for at (or above) ourselves
  // then return the roleScope (ignoring the requested scope)
  if(levelGap <= 0) return minimizedScope

  // if we already have descendent scope then we are ok
  else if(roleScope == ROLE_SCOPE_DESCENDENTS) return minimizedScope
  // now we check how far down the request is compared to what we can do
  // so they can only see directly below themselves
  // if the levelGap is 1 (i.e. they are asking for contacts for directly below themselves)
  // then we return SELF
  // otherwise we don't allow
  else if(roleScope == ROLE_SCOPE_CHILDREN) {
    return levelGap == 1 ?
      ROLE_SCOPE_SELF :
      ROLE_SCOPE_NONE
  }
  // this must mean they only have SELF and are asking for something below that
  else {
    return ROLE_SCOPE_NONE
  }
}

// tells you if the given role scope covers an org path
export const hasScope = (
  userOrgPath: string,
  targetOrgPath: string,
  roleScope: IField_Role_Scope,
): boolean => {
  // how many levels down from the user org are we looking?
  const sourceLevel = userOrgPath.split('.').length
  const targetLevel = targetOrgPath.split('.').length

  // prevent looking upwards
  if(targetLevel < sourceLevel) return false
  
  const levelGap = targetLevel - sourceLevel

  // at the same level
  // so let's just compare the paths
  if(levelGap <= 0) {
    return userOrgPath == targetOrgPath
  }
  else if(roleScope == ROLE_SCOPE_DESCENDENTS) return true
  else if(roleScope == ROLE_SCOPE_CHILDREN) {
    return levelGap == 1 ?
      true :
      false
  }
  else {
    return false
  }
}

export const hasUserSummaryScope = (
  userSummary: IData_UserSummary | null,
  roleName: string,
  targetOrgPath: string | undefined,
): boolean => {
  if(!userSummary) return false
  if(!targetOrgPath) return false
  const role = userSummary.roles[roleName]
  if(!role) return false
  const userOrgPath = userSummary.orgNode.org_path
  return hasScope(
    userOrgPath,
    targetOrgPath,
    role.scope,
  )
}

export const getRoleSummaryTree = (
  roleTree: ITree_Role,
  roleSummary: IData_UserSummary_FlatRoleObject[],
  flatten = true,
): IData_RoleSummary_TreeNode[] => {
  if(!roleTree) return []
  const collectRole = (role: INode_Role): IData_RoleSummary_TreeNode => {
    return {
      name: role.name,
      children: role.roles.map(collectRole),
    }
  }
  return roleSummary.reduce<IData_RoleSummary_TreeNode[]>((all, summary) => {
    const [ ns ] = summary.name.split(':')
    let addItem: IData_RoleSummary_TreeNode | null = null
    if(flatten) {
      const [ ns ] = summary.name.split(':')
      addItem = all.find(r => r.name == ns) || null
    }
    if(!addItem) {
      addItem = {
        name: ns,
        children: [],
        source: summary
      }
      all.push(addItem)
    }
    const roleNode = roleTree.info.mapName[summary.name]
    if(roleNode) {
      addItem.children.push(collectRole(roleNode))
    }
    return all
  }, [])
}

// get a list of roles that are part of the given namespace
export const getSummaryNamespaceRoles = (
  summary: IData_UserSummary,
  namespace: string,
  append = ':',
) => {
  return summary.flatRoles
    .filter(role => {
      return role.name.startsWith(`${namespace}${append}`)
    })
}

// get the first role in a summary that is part of the given namespace
export const getSummaryNamespaceRole = (
  summary: IData_UserSummary,
  namespace: string,
  append = ':',
) => {
  const roleName = Object
    .keys(summary.roles)
    .find(name => name.indexOf(`${namespace}${append}`) == 0)
  if(!roleName) return null
  return summary.roles[roleName]
}

// tells you if a role came from org level or user level
// for example - if the role came from a persona - it's user level
export const isRoleOriginOrg = (
  role: IData_UserSummary_Role,
): boolean => {
  if(!role) return false
  return role.origin.type == ROLE_ORIGIN_TYPE_USER || role.origin.type == ROLE_ORIGIN_TYPE_USER_PERSONA ?
    false :
    true
}

export const hasRoleAccessToType = (role: IData_UserSummary_Role | IData_UserSummary_FlatRoleObject, type: string): boolean => {
  if(!role.meta || !role.meta.types) return true
  return role.meta.types.find((t: string) => t == type) ? true : false
}